import React from "react";

const Pagination = ({ 
  currentPage = 1, 
  totalPages = 1, 
  totalItems = 0, 
  itemsPerPage = 10, 
  onPageChange 
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5; // Maximum number of page buttons to show
    const halfVisible = Math.floor(maxVisible / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    // Adjust start page if end page is at max
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border-t text-sm text-slate-500">
      <p className="text-xs">
        Showing {startItem} to {endItem} of {totalItems} entries
      </p>

      <div className="flex gap-2">
        {/* Previous button */}
        <button
          onClick={() => onPageChange && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
            currentPage === 1
              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
              : "bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-primary"
          }`}
        >
          <span className="material-symbols-outlined text-lg">chevron_left</span>
        </button>

        {/* First page button (if not visible) */}
        {pageNumbers[0] > 1 && (
          <>
            <button
              onClick={() => onPageChange && onPageChange(1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-primary transition-colors"
            >
              1
            </button>
            {pageNumbers[0] > 2 && (
              <span className="w-8 h-8 flex items-center justify-center text-slate-400">...</span>
            )}
          </>
        )}

        {/* Page numbers */}
        {pageNumbers.map((pageNum) => (
          <button
            key={pageNum}
            onClick={() => onPageChange && onPageChange(pageNum)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg font-medium transition-colors ${
              currentPage === pageNum
                ? "bg-primary text-white shadow-sm"
                : "bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-primary"
            }`}
          >
            {pageNum}
          </button>
        ))}

        {/* Last page button (if not visible) */}
        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <>
            {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
              <span className="w-8 h-8 flex items-center justify-center text-slate-400">...</span>
            )}
            <button
              onClick={() => onPageChange && onPageChange(totalPages)}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-primary transition-colors"
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next button */}
        <button
          onClick={() => onPageChange && onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
            currentPage === totalPages
              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
              : "bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-primary"
          }`}
        >
          <span className="material-symbols-outlined text-lg">chevron_right</span>
        </button>
      </div>
    </div>
  );
};

export default Pagination;